// Brain — the per-repo accumulating memory (Phase 0: capture + observe +
// curate). Lists harvested facts; a human Accepts / Rejects / marks Stale them.
// The read path is OFF in the backend — nothing here feeds back into runs yet
// (that's Phase 1). Server-side pagination + search + filters, same shape as
// the Runs tab: the endpoint takes limit/offset/query/status/kind/repoId and
// returns { items, total, limit, offset }.

import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import Shell from "./Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  RefreshButton,
  Spinner,
  apiErrorMessage,
  relativeTime
} from "./shared";

const PAGE_SIZE = 25;

// Status filter tabs. Default to PROPOSED — the curation queue is the point.
const FILTERS = [
  ["PROPOSED", "Proposed"],
  ["ACCEPTED", "Accepted"],
  ["REJECTED", "Rejected"],
  ["STALE", "Stale"],
  ["", "All"]
];

// Kind → pill color (re-verifiable (A)-fact taxonomy; no "verdict" kind).
const KIND_COLOR = {
  CONVENTION: "var(--bss-info)",
  ARCHITECTURE: "var(--bss-boom-pink)",
  GOTCHA: "var(--bss-danger)",
  CONTRACT: "var(--bss-warning)",
  GLOSSARY: "var(--bss-fg-subtle)",
  PLAYBOOK: "var(--bss-success)"
};

// Kinds offered in the manual "Add to brain" form. PLAYBOOK first — it's the
// main hand-authored kind (task-triggered "when working on X, reuse/watch Y").
const KIND_OPTIONS = [
  ["PLAYBOOK", "Playbook — when working on X, reuse / watch Y"],
  ["CONVENTION", "Convention — a coding / review house rule"],
  ["ARCHITECTURE", "Architecture — how the system is structured"],
  ["GOTCHA", "Gotcha — a trap / pitfall"],
  ["CONTRACT", "Contract — an API / interface contract"],
  ["GLOSSARY", "Glossary — a term definition"]
];

// Review lenses a fact routes to (matches the fan-out reviewers). The human
// picks/refines this so each specialized agent gets only its relevant criteria.
const LENS_OPTIONS = [
  ["logical", "Logical / behavioral-flow"],
  ["correctness", "Correctness & data-integrity"],
  ["security", "Security & secrets"],
  ["contract", "API / cross-repo contract"],
  ["performance", "Performance & queries"]
];

const LENS_COLOR = {
  logical: "var(--bss-boom-pink)",
  correctness: "var(--bss-info)",
  security: "var(--bss-danger)",
  contract: "var(--bss-warning)",
  performance: "var(--bss-success)"
};

// Client mirror of the server's lensForKind default (so the dropdown
// pre-selects sensibly; the human can override).
function defaultLensForKind(kind) {
  if (kind === "PLAYBOOK" || kind === "ARCHITECTURE") return "logical";
  if (kind === "CONTRACT") return "contract";
  if (kind === "CONVENTION" || kind === "GOTCHA") return "correctness";
  return "logical";
}

const FIELD_LABEL = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
  color: "var(--bss-fg-muted)"
};
const FIELD_HINT = {
  fontWeight: 400,
  fontSize: 11,
  color: "var(--bss-fg-subtle)"
};

const STATUS_COLOR = {
  PROPOSED: "var(--bss-warning)",
  ACCEPTED: "var(--bss-success)",
  REJECTED: "var(--bss-fg-subtle)",
  STALE: "var(--bss-fg-subtle)",
  SUPERSEDED: "var(--bss-fg-subtle)"
};

// Where a rule came from. `human_review` = harvested from human PR-review
// threads; `run` = post-run memory harvest; `doc` = AGENTS.md/CLAUDE.md seed;
// `human` = hand-added. Surfaced as an always-visible chip so the two
// producers are distinguishable at a glance.
const SOURCE_META = {
  human_review: { label: "human review", color: "var(--bss-boom-pink)" },
  implementation: { label: "implementation", color: "var(--bss-warning)" },
  run: { label: "run", color: "var(--bss-fg-subtle)" },
  doc: { label: "doc", color: "var(--bss-success)" },
  human: { label: "manual", color: "var(--bss-info)" }
};

// Source filter options (value sent to the API as `source`; "" = all).
const SOURCE_FILTERS = [
  ["", "All sources"],
  ["human_review", "Human review"],
  ["implementation", "Implementation harvest"],
  ["run", "Run harvest"],
  ["doc", "Doc seed"],
  ["human", "Manual"]
];

function Pill({ label, color }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.3,
        color,
        border: `1px solid ${color}`,
        borderRadius: 4,
        padding: "0 5px",
        whiteSpace: "nowrap"
      }}
    >
      {label}
    </span>
  );
}

// Manual "Add to brain" form. Lands the entry ACCEPTED (source "human") so it
// injects into runs immediately. A PLAYBOOK requires its "When" trigger — that
// IS the task-match signal the agent keys off.
function AddBrainModal({
  open,
  onClose,
  onCreated,
  onRefresh,
  repoSuggestions
}) {
  const [repoId, setRepoId] = useState("");
  const [kind, setKind] = useState("PLAYBOOK");
  const [lens, setLens] = useState(defaultLensForKind("PLAYBOOK"));
  const [trigger, setTrigger] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [repoOptions, setRepoOptions] = useState([]);
  // Latest page-derived repoIds, read only as a fetch fallback. Kept in a ref
  // so the open-effect doesn't re-run (and wipe the form) when the parent
  // re-renders on a poll and passes a new repoSuggestions array.
  const repoFallbackRef = useRef(repoSuggestions);
  repoFallbackRef.current = repoSuggestions;

  // Reset the form every time the modal opens, and load the authoritative
  // repo list for the select (fall back to the current page's repoIds if the
  // fetch fails, so the field is never empty).
  useEffect(() => {
    if (!open) return;
    setRepoId("");
    setKind("PLAYBOOK");
    setLens(defaultLensForKind("PLAYBOOK"));
    setTrigger("");
    setTitle("");
    setBody("");
    setError(null);
    setNotice(null);
    setSubmitting(false);
    let cancelled = false;
    api
      .bssRepos()
      .then((res) => {
        if (cancelled) return;
        const ids = ((res && res.data && res.data.repos) || []).map(
          (r) => r.id
        );
        setRepoOptions(ids.length ? ids : repoFallbackRef.current);
      })
      .catch(() => {
        if (!cancelled) setRepoOptions(repoFallbackRef.current);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const isPlaybook = kind === "PLAYBOOK";
  const canSubmit =
    repoId.trim() &&
    title.trim() &&
    body.trim() &&
    (!isPlaybook || trigger.trim()) &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.bssBrainCreate({
        repoId: repoId.trim(),
        kind,
        reviewLens: lens,
        title: title.trim(),
        body: body.trim(),
        trigger: trigger.trim() || undefined
      });
      const result = (res && res.data) || {};
      // created:false ⇒ a matching (title+body) entry already exists; the
      // server left its status untouched (a REJECTED rule stays rejected).
      // Don't pretend we added a fresh, active rule — warn instead.
      if (result.created === false) {
        const status = result.status || "unknown";
        setNotice(
          status === "ACCEPTED"
            ? `A matching entry already exists for ${repoId.trim()} and is already active (ACCEPTED).`
            : `A matching entry already exists for ${repoId.trim()} with status ${status}. ` +
                `It was NOT added as new and won't inject until you Accept it — find it in the list below.`
        );
        setSubmitting(false);
        if (onRefresh) onRefresh();
        return;
      }
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not add the entry"));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add to brain"
      width={620}
      footer={
        <>
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bss-btn"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? <Spinner /> : "Add (Accepted)"}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error && (
          <div style={{ color: "var(--bss-danger)", fontSize: 13 }}>
            {error}
          </div>
        )}
        {notice && (
          <div style={{ color: "var(--bss-warning)", fontSize: 13 }}>
            {notice}
          </div>
        )}
        <label style={FIELD_LABEL}>
          Repo
          <select
            className="bss-input"
            value={repoId}
            onChange={(e) => setRepoId(e.target.value)}
          >
            <option value="" disabled>
              Select a repo…
            </option>
            {repoOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label style={FIELD_LABEL}>
          Kind
          <select
            className="bss-input"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              // Re-default the lens to match the new kind (human can override).
              setLens(defaultLensForKind(e.target.value));
            }}
          >
            {KIND_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label style={FIELD_LABEL}>
          Review lens
          <select
            className="bss-input"
            value={lens}
            onChange={(e) => setLens(e.target.value)}
          >
            {LENS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span style={FIELD_HINT}>
            Which specialized reviewer this informs — routes it to that agent so
            each gets only its relevant criteria. Defaulted from the kind;
            refine it to make the agent sharper.
          </span>
        </label>
        <label style={FIELD_LABEL}>
          When{" "}
          {isPlaybook && <span style={{ color: "var(--bss-danger)" }}>*</span>}
          <textarea
            className="bss-input"
            rows={3}
            style={{ resize: "vertical", minHeight: 56 }}
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            placeholder="The task, feature, or area this applies to. The agent compares the work it's doing against this to decide whether to follow the guidance below."
          />
          <span style={FIELD_HINT}>
            The task this applies to — the agent matches on this to decide when
            to follow the guidance.
          </span>
        </label>
        <label style={FIELD_LABEL}>
          Title
          <input
            className="bss-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="A short, memorable name for this rule — shown as the heading in the agent's review criteria."
          />
        </label>
        <label style={FIELD_LABEL}>
          Guidance
          <textarea
            className="bss-input"
            rows={8}
            style={{ resize: "vertical", minHeight: 120 }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What the agent should do when the When matches — e.g. which existing code/capability to reuse instead of rebuilding, what to avoid, and which related settings or logic to account for. Write it as an instruction to the engineer."
          />
        </label>
      </div>
    </Modal>
  );
}

export default function BrainPage() {
  const history = useHistory();
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("PROPOSED");
  const [source, setSource] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // A new filter tab, source, or search term resets to the first page.
  useEffect(() => {
    setPage(0);
  }, [filter, source, debouncedQuery]);

  const fetchBrain = useCallback(
    () =>
      api
        .bssBrain({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          query: debouncedQuery || undefined,
          status: filter || undefined,
          source: source || undefined
        })
        .then((res) => res.data),
    [page, debouncedQuery, filter, source]
  );
  const { data, error, loading, stale, refresh } = useBssPoll(fetchBrain, {
    deps: [page, debouncedQuery, filter, source]
  });

  const entries = (data && data.items) || [];
  const total = (data && data.total) || 0;
  const offset = page * PAGE_SIZE;
  // Repo suggestions for the Add form's datalist — distinct repoIds on the
  // current page. The server validates against the full registry regardless.
  const repoSuggestions = [...new Set(entries.map((e) => e.repoId))].sort();

  const curate = useCallback(
    async (id, status) => {
      setBusyId(id);
      setActionError(null);
      try {
        await api.bssBrainSetStatus(id, status);
        refresh();
      } catch (err) {
        setActionError(apiErrorMessage(err, "Could not update the entry"));
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  let body;
  if (loading) body = <LoadingState label="Loading memory…" />;
  else if (error && !data)
    body = <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />;
  else
    body = (
      <>
        <div className="bss-toolbar">
          <div className="bss-filter-tabs">
            {FILTERS.map(([key, label]) => (
              <button
                key={key || "all"}
                type="button"
                className={`bss-filter-tab ${
                  filter === key ? "is-active" : ""
                }`.trim()}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <select
            className="bss-input"
            style={{ marginLeft: 8, width: "auto", flex: "0 0 auto" }}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            aria-label="Filter by source"
          >
            {SOURCE_FILTERS.map(([value, label]) => (
              <option key={value || "all"} value={value}>
                {label}
              </option>
            ))}
          </select>
          <div className="bss-search-wrap" style={{ flex: 1, marginLeft: 8 }}>
            <Icon.Search size={14} className="bss-icon" />
            <input
              className="bss-input"
              placeholder="Filter by fact text…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {actionError && (
          <div
            className="bss-card"
            style={{ padding: 10, color: "var(--bss-danger)" }}
          >
            {actionError}
          </div>
        )}

        <div className="bss-card" style={{ overflow: "hidden" }}>
          <table className="bss-table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Kind</th>
                <th>Fact</th>
                <th style={{ width: 150 }}>Repo</th>
                <th style={{ width: 70, textAlign: "right" }}>Seen</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }} aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {entries.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 4
                      }}
                    >
                      <Pill
                        label={m.kind}
                        color={KIND_COLOR[m.kind] || "var(--bss-fg-subtle)"}
                      />
                      {m.source && (
                        <Pill
                          label={
                            (SOURCE_META[m.source] || {}).label || m.source
                          }
                          color={
                            (SOURCE_META[m.source] || {}).color ||
                            "var(--bss-fg-subtle)"
                          }
                        />
                      )}
                      {m.reviewLens && (
                        <Pill
                          label={`▹ ${m.reviewLens}`}
                          color={
                            LENS_COLOR[m.reviewLens] || "var(--bss-fg-subtle)"
                          }
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{m.title}</div>
                    <div
                      style={{
                        color: "var(--bss-fg-muted)",
                        fontSize: 12,
                        marginTop: 2
                      }}
                    >
                      {m.body}
                    </div>
                    {m.trigger && (
                      <div
                        style={{
                          color: "var(--bss-fg-subtle)",
                          fontSize: 11,
                          marginTop: 3
                        }}
                      >
                        when: {m.trigger}
                      </div>
                    )}
                    {(m.path || m.sourceRunId || m.sourcePr) && (
                      <div
                        style={{
                          color: "var(--bss-fg-subtle)",
                          fontSize: 11,
                          marginTop: 3
                        }}
                      >
                        {m.path && (
                          <span style={{ fontFamily: "var(--bss-font-mono)" }}>
                            {m.path}
                            {m.line ? `:${m.line}` : ""}
                          </span>
                        )}
                        {m.path && (m.sourceRunId || m.sourcePr) && " · "}
                        {m.sourceRunId && (
                          <button
                            type="button"
                            onClick={() =>
                              history.push(`/bss/runs/${m.sourceRunId}`)
                            }
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              font: "inherit",
                              color: "var(--bss-info)",
                              textDecoration: "underline",
                              cursor: "pointer"
                            }}
                          >
                            source run
                          </button>
                        )}
                        {m.sourcePr && (
                          <a
                            href={`https://github.com/boompay/${m.repoId}/pull/${m.sourcePr}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: "var(--bss-info)",
                              textDecoration: "underline"
                            }}
                          >
                            review PR #{m.sourcePr}
                          </a>
                        )}
                        {" · "}
                        {relativeTime(m.updatedAt)}
                      </div>
                    )}
                  </td>
                  <td
                    style={{ fontFamily: "var(--bss-font-mono)", fontSize: 12 }}
                  >
                    {m.repoId}
                  </td>
                  <td style={{ textAlign: "right" }}>{m.corroboration}</td>
                  <td>
                    <Pill
                      label={m.status}
                      color={STATUS_COLOR[m.status] || "var(--bss-fg-subtle)"}
                    />
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        justifyContent: "flex-end"
                      }}
                    >
                      {m.status !== "ACCEPTED" && (
                        <button
                          type="button"
                          className="bss-btn bss-btn--ghost"
                          disabled={busyId === m.id}
                          onClick={() => curate(m.id, "accepted")}
                        >
                          Accept
                        </button>
                      )}
                      {m.status !== "REJECTED" && (
                        <button
                          type="button"
                          className="bss-btn bss-btn--ghost"
                          disabled={busyId === m.id}
                          onClick={() => curate(m.id, "rejected")}
                        >
                          Reject
                        </button>
                      )}
                      {m.status !== "STALE" && (
                        <button
                          type="button"
                          className="bss-btn bss-btn--ghost"
                          disabled={busyId === m.id}
                          onClick={() => curate(m.id, "stale")}
                        >
                          Stale
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && (
            <EmptyState
              title="No memory entries"
              hint="Facts are harvested from review + implement runs and land here as Proposed for curation."
            />
          )}
        </div>

        <div className="bss-list-foot">
          <div>
            {total === 0
              ? "showing 0 of 0"
              : `showing ${offset + 1}–${offset + entries.length} of ${total}`}
            {stale && (
              <span
                style={{ color: "var(--bss-warning, #b7791f)", marginLeft: 8 }}
              >
                · reconnecting — showing last known data
              </span>
            )}
          </div>
          <div className="bss-pager">
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={offset + entries.length >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </>
    );

  return (
    <Shell>
      <div className="bss-page-hd">
        <div>
          <h1>Brain</h1>
          <div className="bss-page-hd-sub">
            Per-repo accumulating memory — durable, re-verifiable facts
            harvested from runs. Curate Proposed → Accepted; the read path is
            off until Phase 1.
          </div>
        </div>
        <div className="bss-page-hd-right">
          <button
            type="button"
            className="bss-btn"
            onClick={() => setShowAdd(true)}
          >
            + Add to brain
          </button>
          <RefreshButton onClick={refresh} loading={loading} />
        </div>
      </div>
      {body}
      <AddBrainModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => {
          setShowAdd(false);
          refresh();
        }}
        onRefresh={refresh}
        repoSuggestions={repoSuggestions}
      />
    </Shell>
  );
}
